/**
 * Countries and Cities Configuration
 *
 * Used for shipping address dropdowns with dependent city selection.
 */

export interface City {
  id: string;
  name: string;
}

export interface Country {
  id: string;
  name: string;
  code: string; // ISO 3166-1 alpha-2
  dialCode: string; // International dialling code
  cities: City[];
}

export const COUNTRIES: Country[] = [
  // Middle East / GCC
  {
    id: 'ae',
    name: 'United Arab Emirates',
    code: 'AE',
    dialCode: '+971',
    cities: [
      { id: 'dubai', name: 'Dubai' },
      { id: 'abu-dhabi', name: 'Abu Dhabi' },
      { id: 'sharjah', name: 'Sharjah' },
      { id: 'ajman', name: 'Ajman' },
      { id: 'ras-al-khaimah', name: 'Ras Al Khaimah' },
      { id: 'fujairah', name: 'Fujairah' },
      { id: 'umm-al-quwain', name: 'Umm Al Quwain' },
    ],
  },
  {
    id: 'sa',
    name: 'Saudi Arabia',
    code: 'SA',
    dialCode: '+966',
    cities: [
      { id: 'riyadh', name: 'Riyadh' },
      { id: 'jeddah', name: 'Jeddah' },
      { id: 'mecca', name: 'Mecca' },
      { id: 'medina', name: 'Medina' },
      { id: 'dammam', name: 'Dammam' },
      { id: 'khobar', name: 'Khobar' },
      { id: 'dhahran', name: 'Dhahran' },
    ],
  },
  {
    id: 'qa',
    name: 'Qatar',
    code: 'QA',
    dialCode: '+974',
    cities: [
      { id: 'doha', name: 'Doha' },
      { id: 'al-wakrah', name: 'Al Wakrah' },
      { id: 'al-khor', name: 'Al Khor' },
      { id: 'lusail', name: 'Lusail' },
    ],
  },
  {
    id: 'kw',
    name: 'Kuwait',
    code: 'KW',
    dialCode: '+965',
    cities: [
      { id: 'kuwait-city', name: 'Kuwait City' },
      { id: 'hawalli', name: 'Hawalli' },
      { id: 'salmiya', name: 'Salmiya' },
      { id: 'farwaniya', name: 'Farwaniya' },
    ],
  },
  {
    id: 'bh',
    name: 'Bahrain',
    code: 'BH',
    dialCode: '+973',
    cities: [
      { id: 'manama', name: 'Manama' },
      { id: 'riffa', name: 'Riffa' },
      { id: 'muharraq', name: 'Muharraq' },
    ],
  },
  {
    id: 'om',
    name: 'Oman',
    code: 'OM',
    dialCode: '+968',
    cities: [
      { id: 'muscat', name: 'Muscat' },
      { id: 'salalah', name: 'Salalah' },
      { id: 'sohar', name: 'Sohar' },
      { id: 'nizwa', name: 'Nizwa' },
    ],
  },

  // North America
  {
    id: 'us',
    name: 'United States',
    code: 'US',
    dialCode: '+1',
    cities: [
      { id: 'new-york', name: 'New York' },
      { id: 'los-angeles', name: 'Los Angeles' },
      { id: 'chicago', name: 'Chicago' },
      { id: 'houston', name: 'Houston' },
      { id: 'phoenix', name: 'Phoenix' },
      { id: 'philadelphia', name: 'Philadelphia' },
      { id: 'san-antonio', name: 'San Antonio' },
      { id: 'san-diego', name: 'San Diego' },
      { id: 'dallas', name: 'Dallas' },
      { id: 'san-jose', name: 'San Jose' },
      { id: 'austin', name: 'Austin' },
      { id: 'jacksonville', name: 'Jacksonville' },
      { id: 'san-francisco', name: 'San Francisco' },
      { id: 'seattle', name: 'Seattle' },
      { id: 'denver', name: 'Denver' },
      { id: 'boston', name: 'Boston' },
      { id: 'miami', name: 'Miami' },
      { id: 'atlanta', name: 'Atlanta' },
      { id: 'las-vegas', name: 'Las Vegas' },
      { id: 'washington-dc', name: 'Washington D.C.' },
    ],
  },
  {
    id: 'ca',
    name: 'Canada',
    code: 'CA',
    dialCode: '+1',
    cities: [
      { id: 'toronto', name: 'Toronto' },
      { id: 'montreal', name: 'Montreal' },
      { id: 'vancouver', name: 'Vancouver' },
      { id: 'calgary', name: 'Calgary' },
      { id: 'edmonton', name: 'Edmonton' },
      { id: 'ottawa', name: 'Ottawa' },
      { id: 'winnipeg', name: 'Winnipeg' },
      { id: 'quebec-city', name: 'Quebec City' },
    ],
  },

  // Europe
  {
    id: 'gb',
    name: 'United Kingdom',
    code: 'GB',
    dialCode: '+44',
    cities: [
      { id: 'london', name: 'London' },
      { id: 'birmingham', name: 'Birmingham' },
      { id: 'manchester', name: 'Manchester' },
      { id: 'glasgow', name: 'Glasgow' },
      { id: 'liverpool', name: 'Liverpool' },
      { id: 'leeds', name: 'Leeds' },
      { id: 'edinburgh', name: 'Edinburgh' },
      { id: 'bristol', name: 'Bristol' },
      { id: 'cardiff', name: 'Cardiff' },
      { id: 'belfast', name: 'Belfast' },
    ],
  },
  {
    id: 'de',
    name: 'Germany',
    code: 'DE',
    dialCode: '+49',
    cities: [
      { id: 'berlin', name: 'Berlin' },
      { id: 'hamburg', name: 'Hamburg' },
      { id: 'munich', name: 'Munich' },
      { id: 'cologne', name: 'Cologne' },
      { id: 'frankfurt', name: 'Frankfurt' },
      { id: 'stuttgart', name: 'Stuttgart' },
      { id: 'dusseldorf', name: 'Dusseldorf' },
      { id: 'dortmund', name: 'Dortmund' },
    ],
  },
  {
    id: 'fr',
    name: 'France',
    code: 'FR',
    dialCode: '+33',
    cities: [
      { id: 'paris', name: 'Paris' },
      { id: 'marseille', name: 'Marseille' },
      { id: 'lyon', name: 'Lyon' },
      { id: 'toulouse', name: 'Toulouse' },
      { id: 'nice', name: 'Nice' },
      { id: 'nantes', name: 'Nantes' },
      { id: 'strasbourg', name: 'Strasbourg' },
      { id: 'bordeaux', name: 'Bordeaux' },
    ],
  },
  {
    id: 'it',
    name: 'Italy',
    code: 'IT',
    dialCode: '+39',
    cities: [
      { id: 'rome', name: 'Rome' },
      { id: 'milan', name: 'Milan' },
      { id: 'naples', name: 'Naples' },
      { id: 'turin', name: 'Turin' },
      { id: 'florence', name: 'Florence' },
      { id: 'venice', name: 'Venice' },
      { id: 'bologna', name: 'Bologna' },
    ],
  },
  {
    id: 'es',
    name: 'Spain',
    code: 'ES',
    dialCode: '+34',
    cities: [
      { id: 'madrid', name: 'Madrid' },
      { id: 'barcelona', name: 'Barcelona' },
      { id: 'valencia', name: 'Valencia' },
      { id: 'seville', name: 'Seville' },
      { id: 'bilbao', name: 'Bilbao' },
      { id: 'malaga', name: 'Malaga' },
    ],
  },
  {
    id: 'nl',
    name: 'Netherlands',
    code: 'NL',
    dialCode: '+31',
    cities: [
      { id: 'amsterdam', name: 'Amsterdam' },
      { id: 'rotterdam', name: 'Rotterdam' },
      { id: 'the-hague', name: 'The Hague' },
      { id: 'utrecht', name: 'Utrecht' },
      { id: 'eindhoven', name: 'Eindhoven' },
    ],
  },
  {
    id: 'ch',
    name: 'Switzerland',
    code: 'CH',
    dialCode: '+41',
    cities: [
      { id: 'zurich', name: 'Zurich' },
      { id: 'geneva', name: 'Geneva' },
      { id: 'basel', name: 'Basel' },
      { id: 'bern', name: 'Bern' },
      { id: 'lausanne', name: 'Lausanne' },
    ],
  },
  {
    id: 'at',
    name: 'Austria',
    code: 'AT',
    dialCode: '+43',
    cities: [
      { id: 'vienna', name: 'Vienna' },
      { id: 'salzburg', name: 'Salzburg' },
      { id: 'innsbruck', name: 'Innsbruck' },
      { id: 'graz', name: 'Graz' },
    ],
  },
  {
    id: 'be',
    name: 'Belgium',
    code: 'BE',
    dialCode: '+32',
    cities: [
      { id: 'brussels', name: 'Brussels' },
      { id: 'antwerp', name: 'Antwerp' },
      { id: 'ghent', name: 'Ghent' },
      { id: 'bruges', name: 'Bruges' },
    ],
  },
  {
    id: 'se',
    name: 'Sweden',
    code: 'SE',
    dialCode: '+46',
    cities: [
      { id: 'stockholm', name: 'Stockholm' },
      { id: 'gothenburg', name: 'Gothenburg' },
      { id: 'malmo', name: 'Malmo' },
      { id: 'uppsala', name: 'Uppsala' },
    ],
  },
  {
    id: 'no',
    name: 'Norway',
    code: 'NO',
    dialCode: '+47',
    cities: [
      { id: 'oslo', name: 'Oslo' },
      { id: 'bergen', name: 'Bergen' },
      { id: 'trondheim', name: 'Trondheim' },
      { id: 'stavanger', name: 'Stavanger' },
    ],
  },
  {
    id: 'dk',
    name: 'Denmark',
    code: 'DK',
    dialCode: '+45',
    cities: [
      { id: 'copenhagen', name: 'Copenhagen' },
      { id: 'aarhus', name: 'Aarhus' },
      { id: 'odense', name: 'Odense' },
    ],
  },
  {
    id: 'ie',
    name: 'Ireland',
    code: 'IE',
    dialCode: '+353',
    cities: [
      { id: 'dublin', name: 'Dublin' },
      { id: 'cork', name: 'Cork' },
      { id: 'galway', name: 'Galway' },
      { id: 'limerick', name: 'Limerick' },
    ],
  },
  {
    id: 'pt',
    name: 'Portugal',
    code: 'PT',
    dialCode: '+351',
    cities: [
      { id: 'lisbon', name: 'Lisbon' },
      { id: 'porto', name: 'Porto' },
      { id: 'faro', name: 'Faro' },
    ],
  },
  {
    id: 'pl',
    name: 'Poland',
    code: 'PL',
    dialCode: '+48',
    cities: [
      { id: 'warsaw', name: 'Warsaw' },
      { id: 'krakow', name: 'Krakow' },
      { id: 'gdansk', name: 'Gdansk' },
      { id: 'wroclaw', name: 'Wroclaw' },
    ],
  },
  {
    id: 'gr',
    name: 'Greece',
    code: 'GR',
    dialCode: '+30',
    cities: [
      { id: 'athens', name: 'Athens' },
      { id: 'thessaloniki', name: 'Thessaloniki' },
      { id: 'heraklion', name: 'Heraklion' },
    ],
  },

  // Asia Pacific
  {
    id: 'au',
    name: 'Australia',
    code: 'AU',
    dialCode: '+61',
    cities: [
      { id: 'sydney', name: 'Sydney' },
      { id: 'melbourne', name: 'Melbourne' },
      { id: 'brisbane', name: 'Brisbane' },
      { id: 'perth', name: 'Perth' },
      { id: 'adelaide', name: 'Adelaide' },
      { id: 'gold-coast', name: 'Gold Coast' },
      { id: 'canberra', name: 'Canberra' },
    ],
  },
  {
    id: 'nz',
    name: 'New Zealand',
    code: 'NZ',
    dialCode: '+64',
    cities: [
      { id: 'auckland', name: 'Auckland' },
      { id: 'wellington', name: 'Wellington' },
      { id: 'christchurch', name: 'Christchurch' },
      { id: 'queenstown', name: 'Queenstown' },
    ],
  },
  {
    id: 'sg',
    name: 'Singapore',
    code: 'SG',
    dialCode: '+65',
    cities: [
      { id: 'singapore', name: 'Singapore' },
    ],
  },
  {
    id: 'hk',
    name: 'Hong Kong',
    code: 'HK',
    dialCode: '+852',
    cities: [
      { id: 'hong-kong', name: 'Hong Kong' },
      { id: 'kowloon', name: 'Kowloon' },
    ],
  },
  {
    id: 'jp',
    name: 'Japan',
    code: 'JP',
    dialCode: '+81',
    cities: [
      { id: 'tokyo', name: 'Tokyo' },
      { id: 'osaka', name: 'Osaka' },
      { id: 'kyoto', name: 'Kyoto' },
      { id: 'yokohama', name: 'Yokohama' },
      { id: 'nagoya', name: 'Nagoya' },
      { id: 'sapporo', name: 'Sapporo' },
      { id: 'fukuoka', name: 'Fukuoka' },
    ],
  },
  {
    id: 'kr',
    name: 'South Korea',
    code: 'KR',
    dialCode: '+82',
    cities: [
      { id: 'seoul', name: 'Seoul' },
      { id: 'busan', name: 'Busan' },
      { id: 'incheon', name: 'Incheon' },
      { id: 'daegu', name: 'Daegu' },
    ],
  },
  {
    id: 'cn',
    name: 'China',
    code: 'CN',
    dialCode: '+86',
    cities: [
      { id: 'shanghai', name: 'Shanghai' },
      { id: 'beijing', name: 'Beijing' },
      { id: 'shenzhen', name: 'Shenzhen' },
      { id: 'guangzhou', name: 'Guangzhou' },
      { id: 'chengdu', name: 'Chengdu' },
      { id: 'hangzhou', name: 'Hangzhou' },
    ],
  },
  {
    id: 'in',
    name: 'India',
    code: 'IN',
    dialCode: '+91',
    cities: [
      { id: 'mumbai', name: 'Mumbai' },
      { id: 'delhi', name: 'Delhi' },
      { id: 'bangalore', name: 'Bangalore' },
      { id: 'hyderabad', name: 'Hyderabad' },
      { id: 'chennai', name: 'Chennai' },
      { id: 'kolkata', name: 'Kolkata' },
      { id: 'pune', name: 'Pune' },
      { id: 'ahmedabad', name: 'Ahmedabad' },
    ],
  },
  {
    id: 'my',
    name: 'Malaysia',
    code: 'MY',
    dialCode: '+60',
    cities: [
      { id: 'kuala-lumpur', name: 'Kuala Lumpur' },
      { id: 'penang', name: 'Penang' },
      { id: 'johor-bahru', name: 'Johor Bahru' },
      { id: 'malacca', name: 'Malacca' },
    ],
  },
  {
    id: 'th',
    name: 'Thailand',
    code: 'TH',
    dialCode: '+66',
    cities: [
      { id: 'bangkok', name: 'Bangkok' },
      { id: 'chiang-mai', name: 'Chiang Mai' },
      { id: 'phuket', name: 'Phuket' },
      { id: 'pattaya', name: 'Pattaya' },
    ],
  },
  {
    id: 'id',
    name: 'Indonesia',
    code: 'ID',
    dialCode: '+62',
    cities: [
      { id: 'jakarta', name: 'Jakarta' },
      { id: 'bali', name: 'Bali' },
      { id: 'surabaya', name: 'Surabaya' },
      { id: 'bandung', name: 'Bandung' },
    ],
  },
  {
    id: 'ph',
    name: 'Philippines',
    code: 'PH',
    dialCode: '+63',
    cities: [
      { id: 'manila', name: 'Manila' },
      { id: 'cebu', name: 'Cebu' },
      { id: 'davao', name: 'Davao' },
    ],
  },
  {
    id: 'vn',
    name: 'Vietnam',
    code: 'VN',
    dialCode: '+84',
    cities: [
      { id: 'ho-chi-minh', name: 'Ho Chi Minh City' },
      { id: 'hanoi', name: 'Hanoi' },
      { id: 'da-nang', name: 'Da Nang' },
    ],
  },

  // Africa
  {
    id: 'za',
    name: 'South Africa',
    code: 'ZA',
    dialCode: '+27',
    cities: [
      { id: 'johannesburg', name: 'Johannesburg' },
      { id: 'cape-town', name: 'Cape Town' },
      { id: 'durban', name: 'Durban' },
      { id: 'pretoria', name: 'Pretoria' },
    ],
  },
  {
    id: 'eg',
    name: 'Egypt',
    code: 'EG',
    dialCode: '+20',
    cities: [
      { id: 'cairo', name: 'Cairo' },
      { id: 'alexandria', name: 'Alexandria' },
      { id: 'giza', name: 'Giza' },
      { id: 'sharm-el-sheikh', name: 'Sharm El Sheikh' },
    ],
  },
  {
    id: 'ma',
    name: 'Morocco',
    code: 'MA',
    dialCode: '+212',
    cities: [
      { id: 'casablanca', name: 'Casablanca' },
      { id: 'marrakech', name: 'Marrakech' },
      { id: 'rabat', name: 'Rabat' },
      { id: 'fes', name: 'Fes' },
    ],
  },
  {
    id: 'ng',
    name: 'Nigeria',
    code: 'NG',
    dialCode: '+234',
    cities: [
      { id: 'lagos', name: 'Lagos' },
      { id: 'abuja', name: 'Abuja' },
      { id: 'kano', name: 'Kano' },
    ],
  },
  {
    id: 'ke',
    name: 'Kenya',
    code: 'KE',
    dialCode: '+254',
    cities: [
      { id: 'nairobi', name: 'Nairobi' },
      { id: 'mombasa', name: 'Mombasa' },
    ],
  },

  // South America
  {
    id: 'br',
    name: 'Brazil',
    code: 'BR',
    dialCode: '+55',
    cities: [
      { id: 'sao-paulo', name: 'Sao Paulo' },
      { id: 'rio-de-janeiro', name: 'Rio de Janeiro' },
      { id: 'brasilia', name: 'Brasilia' },
      { id: 'salvador', name: 'Salvador' },
      { id: 'fortaleza', name: 'Fortaleza' },
    ],
  },
  {
    id: 'mx',
    name: 'Mexico',
    code: 'MX',
    dialCode: '+52',
    cities: [
      { id: 'mexico-city', name: 'Mexico City' },
      { id: 'guadalajara', name: 'Guadalajara' },
      { id: 'monterrey', name: 'Monterrey' },
      { id: 'cancun', name: 'Cancun' },
    ],
  },
  {
    id: 'ar',
    name: 'Argentina',
    code: 'AR',
    dialCode: '+54',
    cities: [
      { id: 'buenos-aires', name: 'Buenos Aires' },
      { id: 'cordoba', name: 'Cordoba' },
      { id: 'mendoza', name: 'Mendoza' },
    ],
  },
  {
    id: 'cl',
    name: 'Chile',
    code: 'CL',
    dialCode: '+56',
    cities: [
      { id: 'santiago', name: 'Santiago' },
      { id: 'valparaiso', name: 'Valparaiso' },
    ],
  },
  {
    id: 'co',
    name: 'Colombia',
    code: 'CO',
    dialCode: '+57',
    cities: [
      { id: 'bogota', name: 'Bogota' },
      { id: 'medellin', name: 'Medellin' },
      { id: 'cartagena', name: 'Cartagena' },
    ],
  },
  {
    id: 'pe',
    name: 'Peru',
    code: 'PE',
    dialCode: '+51',
    cities: [
      { id: 'lima', name: 'Lima' },
      { id: 'cusco', name: 'Cusco' },
    ],
  },

  // Other
  {
    id: 'tr',
    name: 'Turkey',
    code: 'TR',
    dialCode: '+90',
    cities: [
      { id: 'istanbul', name: 'Istanbul' },
      { id: 'ankara', name: 'Ankara' },
      { id: 'izmir', name: 'Izmir' },
      { id: 'antalya', name: 'Antalya' },
    ],
  },
  {
    id: 'ru',
    name: 'Russia',
    code: 'RU',
    dialCode: '+7',
    cities: [
      { id: 'moscow', name: 'Moscow' },
      { id: 'saint-petersburg', name: 'Saint Petersburg' },
      { id: 'novosibirsk', name: 'Novosibirsk' },
    ],
  },
  {
    id: 'il',
    name: 'Israel',
    code: 'IL',
    dialCode: '+972',
    cities: [
      { id: 'tel-aviv', name: 'Tel Aviv' },
      { id: 'jerusalem', name: 'Jerusalem' },
      { id: 'haifa', name: 'Haifa' },
    ],
  },
];

// Helper functions
export function getCountryById(countryId: string): Country | undefined {
  return COUNTRIES.find(c => c.id === countryId);
}

export function getCountryByName(countryName: string): Country | undefined {
  return COUNTRIES.find(c => c.name.toLowerCase() === countryName.toLowerCase());
}

export function getCitiesByCountry(countryId: string): City[] {
  const country = getCountryById(countryId);
  return country?.cities || [];
}

export function getCityById(countryId: string, cityId: string): City | undefined {
  const cities = getCitiesByCountry(countryId);
  return cities.find(c => c.id === cityId);
}

export function getDialCodeByCountryName(countryName: string): string | undefined {
  const country = getCountryByName(countryName);
  return country?.dialCode;
}

// Sorted countries for dropdown
export const SORTED_COUNTRIES = [...COUNTRIES].sort((a, b) => a.name.localeCompare(b.name));

// Popular countries (shown at top of dropdown)
export const POPULAR_COUNTRY_IDS = ['ae', 'us', 'gb', 'sa', 'qa', 'de', 'fr', 'au', 'sg', 'in'];

export function getPopularCountries(): Country[] {
  return POPULAR_COUNTRY_IDS
    .map(id => getCountryById(id))
    .filter((c): c is Country => c !== undefined);
}

export function getOtherCountries(): Country[] {
  return SORTED_COUNTRIES.filter(c => !POPULAR_COUNTRY_IDS.includes(c.id));
}
